import React, { useState, useEffect, useMemo } from 'react';
import {
    Container, Tabs, Tab, Box, Typography, Card, CardContent,
    Chip, Button, Skeleton, Divider, Paper, Stack, Avatar,
    Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, TextField, CircularProgress, MenuItem,
    Collapse, IconButton, InputAdornment, Tooltip, Grid
} from '@mui/material';
import { useNavigate }    from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DeleteForeverIcon  from '@mui/icons-material/DeleteForever';
import CalendarTodayIcon  from '@mui/icons-material/CalendarToday';
import GroupIcon          from '@mui/icons-material/Group';
import SortIcon           from '@mui/icons-material/Sort';
import FilterListIcon     from '@mui/icons-material/FilterList';
import CloseIcon          from '@mui/icons-material/Close';
import SearchIcon         from '@mui/icons-material/Search';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import AccessTimeIcon     from '@mui/icons-material/AccessTime';
import axios              from '../api/axiosConfig';
import { useAuth }        from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';

const TERRACOTTA = '#C4622D';

const CUSTOMER_TABS = ['DRAFT','RESERVED','PAID','REFUNDED','CANCELED'];
const MANAGER_TABS  = ['DRAFT','RESERVED','PAID','REFUNDED','CANCELED','DELETED'];

function BookingCardSkeleton() {
    return (
        <Card elevation={0} sx={{ display:'flex', flexDirection:{ xs:'column', sm:'row' },
            border:'1px solid', borderColor:'divider', overflow:'hidden' }}>
            <CardContent sx={{ flex:1, p:2.5 }}>
                <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1.5 }}>
                    <Skeleton variant="text" width={200} height={28}/>
                    <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius:4 }}/>
                </Box>
                <Stack direction="row" spacing={3} mt={1}>
                    {[1,2].map(i => (
                        <Box key={i} sx={{ display:'flex', alignItems:'center', gap:0.6 }}>
                            <Skeleton variant="circular" width={16} height={16}/>
                            <Box><Skeleton variant="text" width={50} height={14}/><Skeleton variant="text" width={80} height={18}/></Box>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
            <Divider orientation="vertical" flexItem sx={{ display:{ xs:'none', sm:'block' } }}/>
            <Box sx={{ p:2.5, display:'flex', flexDirection:'column', alignItems:'flex-end', minWidth:180, bgcolor:'rgba(250,246,240,0.6)' }}>
                <Skeleton variant="text" width={40} height={14}/>
                <Skeleton variant="text" width={90} height={36}/>
                <Skeleton variant="rounded" width={120} height={32} sx={{ borderRadius:8, mt:1.5 }}/>
            </Box>
        </Card>
    );
}

const MyBookings = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { t }    = useTranslation();
    const { format, convert } = useCurrency();

    const [bookings,      setBookings]      = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [rentals,       setRentals]       = useState([]);
    const [rentalsLoading, setRentalsLoading] = useState(true);
    const [tabValue,      setTabValue]      = useState(0);
    const [deleteDialog,  setDeleteDialog]  = useState({ open:false, bookingId:null, tourName:'' });
    const [deleteInput,   setDeleteInput]   = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [payingBalanceId, setPayingBalanceId] = useState(null);

    // ── Sort state ────────────────────────────────────────────────────────────
    const [sortBy,  setSortBy]  = useState('date_desc');  // date_desc | date_asc | total_desc | total_asc | pax_desc | pax_asc
    const [showSort, setShowSort] = useState(false);

    // ── Manager filter state ──────────────────────────────────────────────────
    const [showFilter,       setShowFilter]       = useState(false);
    const [filterCustomer,   setFilterCustomer]   = useState('');
    const [filterTour,       setFilterTour]       = useState('');
    const [filterTotalMin,   setFilterTotalMin]   = useState('');
    const [filterTotalMax,   setFilterTotalMax]   = useState('');
    const [filterDateFrom,   setFilterDateFrom]   = useState('');
    const [filterDateTo,     setFilterDateTo]     = useState('');
    const [filterTourType,   setFilterTourType]   = useState('');
    const [filterPlace,      setFilterPlace]      = useState('');

    const isManager      = user?.role === 'MANAGER' || user?.role === 'ADMIN';
    const TAB_CATEGORIES = isManager ? MANAGER_TABS : CUSTOMER_TABS;

    const getStatusConfig = (status) => {
        const map = {
            DRAFT:            { color:'warning',   label:t('my_bookings.status_pending'),        action:isManager ? t('my_bookings.action_view_draft_mgr') : t('my_bookings.action_complete') },
            RESERVED:         { color:'info',      label:'Reserved (30% paid)',                  action:'View' },
            PAID:             { color:'success',   label:t('my_bookings.status_paid'),           action:t('my_bookings.action_view') },
            REFUNDED:         { color:'info',      label:t('my_bookings.status_refunded'),       action:t('my_bookings.action_view_refund') },
            CANCELED:         { color:'error',     label:t('my_bookings.status_canceled'),       action:t('my_bookings.action_view_canceled') },
            REFUND_REQUESTED: { color:'secondary', label:t('my_bookings.status_refund_pending'), action:t('my_bookings.action_view') },
            DELETED:          { color:'error',     label:t('my_bookings.status_deleted'),        action:t('my_bookings.action_view') },
        };
        return map[status] || { color:'default', label:status, action:t('my_bookings.action_view') };
    };
    const getTabLabel = tab => ({
        DRAFT: t('my_bookings.status_pending'), RESERVED: 'Reserved', PAID: t('my_bookings.status_paid'),
        REFUNDED: t('my_bookings.status_refunded'), CANCELED: t('my_bookings.status_canceled'),
        REFUND_REQUESTED: t('my_bookings.status_refund_pending'), DELETED: t('my_bookings.status_deleted'),
    }[tab] ?? tab);

    // ── Data fetch ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!user) { setRentalsLoading(false); return; }
        axios.get('/rentals/view/my')
            .then(res => setRentals(Array.isArray(res.data) ? res.data : []))
            .catch(() => setRentals([]))
            .finally(() => setRentalsLoading(false));
    }, [user]);

    useEffect(() => {
        const fetchBookings = async () => {
            if (!user) return;
            try {
                const billsRes = isManager
                    ? await axios.get('/bills/view?size=1000')
                    : await axios.get(`/bills/view/customer/${user.id}`);
                const bills = Array.isArray(billsRes.data)
                    ? billsRes.data
                    : (billsRes.data?.content || []);

                const detailed = await Promise.all(bills.map(async bill => {
                    let tourName = t('my_bookings.unknown_tour');
                    let tourType = null, tourPlace = null;
                    let participantCount = 0;
                    if (bill.offerType === 'RENTAL') {
                        const date = bill.created_at || bill.createdAt;
                        const dateStr = date
                            ? new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                            : '';
                        tourName = dateStr ? `Bike Rental · ${dateStr}` : 'Bike Rental';
                    } else {
                        try {
                            const offerRes = await axios.get(`/offers/${bill.offerId}`);
                            tourName  = offerRes.data.title;
                            tourType  = offerRes.data.type;
                            tourPlace = offerRes.data.place;
                        } catch {}
                    }
                    try {
                        const itemsRes = await axios.get(`/bill_items/view/bill-id/${bill.id}`);
                        participantCount = itemsRes.data.reduce((s, i) => s + i.quantity, 0);
                    } catch {}
                    return {
                        id: bill.id, tourName, tourType, tourPlace,
                        tourDate:     bill.tourDate     || null,   // "2025-06-15" or null
                        slotStart:    bill.slotStartTime || null,  // "09:00"
                        slotEnd:      bill.slotEndTime   || null,
                        createdAt:    bill.created_at    || bill.createdAt,
                        totalPrice:   bill.totalAmount,
                        currency:     bill.currency || 'USD',
                        participantCount: bill.participantCount || participantCount,
                        billStatus: bill.billStatus,
                        billType:     bill.billType     || null,   // "TOUR", "RENTAL", "BIKE_TRAVEL"
                        startDate:    bill.startDate    || null,   // for BIKE_TRAVEL bookings
                        depositAmount: bill.depositAmount || null,
                        customerName:     bill.customerName     || null,
                        customerEmail:    bill.customerEmail    || null,
                        customerImageUrl: bill.customerImageUrl || null,
                    };
                }));
                setBookings(detailed);
            } catch (err) { console.error('Error fetching bookings:', err); }
            finally { setLoading(false); }
        };
        fetchBookings();
    }, [user, isManager]); // eslint-disable-line react-hooks/exhaustive-deps

    const resetFilters = () => {
        setFilterCustomer(''); setFilterTour(''); setFilterTotalMin('');
        setFilterTotalMax(''); setFilterDateFrom(''); setFilterDateTo('');
        setFilterTourType(''); setFilterPlace('');
    };
    const hasActiveFilters = filterCustomer || filterTour || filterTotalMin || filterTotalMax ||
        filterDateFrom || filterDateTo || filterTourType || filterPlace;

    // ── Derived display list ──────────────────────────────────────────────────
    const displayedBookings = useMemo(() => {
        const currentTab = TAB_CATEGORIES[tabValue];
        let list = bookings.filter(b => {
            if (b.billType === 'RENTAL') return false;
            if (currentTab === 'PAID' && b.billStatus === 'REFUND_REQUESTED') return true;
            return b.billStatus === currentTab;
        });

        // Manager filters
        if (isManager) {
            if (filterCustomer)  list = list.filter(b => b.customerName?.toLowerCase().includes(filterCustomer.toLowerCase()) || b.customerEmail?.toLowerCase().includes(filterCustomer.toLowerCase()));
            if (filterTour)      list = list.filter(b => b.tourName?.toLowerCase().includes(filterTour.toLowerCase()));
            if (filterTourType)  list = list.filter(b => b.tourType === filterTourType);
            if (filterPlace)     list = list.filter(b => b.tourPlace?.toLowerCase().includes(filterPlace.toLowerCase()));
            if (filterTotalMin)  list = list.filter(b => convert(b.totalPrice, b.currency) >= Number(filterTotalMin) * 100);
            if (filterTotalMax)  list = list.filter(b => convert(b.totalPrice, b.currency) <= Number(filterTotalMax) * 100);
            if (filterDateFrom)  list = list.filter(b => b.tourDate && b.tourDate >= filterDateFrom);
            if (filterDateTo)    list = list.filter(b => b.tourDate && b.tourDate <= filterDateTo);
        }

        // Sort
        return [...list].sort((a, b) => {
            switch (sortBy) {
                case 'date_asc':    return new Date(a.tourDate || a.createdAt) - new Date(b.tourDate || b.createdAt);
                case 'total_desc':  return b.totalPrice - a.totalPrice;
                case 'total_asc':   return a.totalPrice - b.totalPrice;
                case 'pax_desc':    return b.participantCount - a.participantCount;
                case 'pax_asc':     return a.participantCount - b.participantCount;
                case 'date_desc':
                default:            return new Date(b.tourDate || b.createdAt) - new Date(a.tourDate || a.createdAt);
            }
        });
    }, [bookings, tabValue, TAB_CATEGORIES, sortBy, isManager, convert,
        filterCustomer, filterTour, filterTourType, filterPlace,
        filterTotalMin, filterTotalMax, filterDateFrom, filterDateTo]);

    const handlePayBalance = async (billId) => {
        setPayingBalanceId(billId);
        try {
            const res = await axios.post(`/api/bike-travel-offers/pay-balance/${billId}`);
            window.location.href = res.data.sessionUrl;
        } catch (err) {
            alert('Could not create payment session: ' + (err.response?.data?.message || err.message));
            setPayingBalanceId(null);
        }
    };

    const handleHardDelete = async () => {
        setDeleteLoading(true);
        try {
            await axios.delete(`/bills/${deleteDialog.bookingId}`);
            setBookings(prev => prev.filter(b => b.id !== deleteDialog.bookingId));
            setDeleteDialog({ open:false, bookingId:null, tourName:'' });
        } catch (err) { alert('Delete failed: ' + (err.response?.data?.message || err.message)); }
        finally { setDeleteLoading(false); }
    };

    const SORT_OPTIONS = [
        { value:'date_desc',  label:'Date (newest first)' },
        { value:'date_asc',   label:'Date (oldest first)' },
        { value:'total_desc', label:'Total (highest first)' },
        { value:'total_asc',  label:'Total (lowest first)' },
        { value:'pax_desc',   label:'Participants (most first)' },
        { value:'pax_asc',    label:'Participants (fewest first)' },
    ];
    const TOUR_TYPES = ['WALK','BIKE','BUS'];

    return (
        <Box sx={{ minHeight: '100vh', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("https://www.originalarmenia.com/wp-content/uploads/2020/04/armenian-wine-areni.jpg") center/cover fixed` }}>
        <Container maxWidth="md" sx={{ mt: { xs: 3, md: 5 }, mb: { xs: 5, md: 8 } }}>

            {/* Header */}
            <Box display="flex" alignItems="baseline" gap={2} mb={2} flexWrap="wrap">
                <Typography variant="h4" fontWeight="800" color="primary"
                            sx={{ fontSize: { xs: '1.6rem', sm: '2.125rem' } }}>
                    {isManager ? t('my_bookings.title_manager') : t('my_bookings.title_customer')}
                </Typography>
                {isManager && (
                    <Typography variant="body2" color="text.secondary">{t('my_bookings.manager_view')}</Typography>
                )}
            </Box>

            {/* Sort & filter bar */}
            <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" alignItems="center">
                {/* Sort */}
                <Box sx={{ position:'relative' }}>
                    <Button size="small" variant="outlined" startIcon={<SortIcon/>}
                            onClick={() => setShowSort(p => !p)}
                            sx={{ color:TERRACOTTA, borderColor:'rgba(196,98,45,0.35)' }}>
                        Sort
                    </Button>
                    {showSort && (
                        <Paper elevation={4} sx={{ position:'absolute', top:'110%', left:0, zIndex:200,
                            minWidth:230, border:'1px solid rgba(196,98,45,0.15)', borderRadius:2 }}>
                            {SORT_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} selected={sortBy === opt.value}
                                          onClick={() => { setSortBy(opt.value); setShowSort(false); }}
                                          sx={{ fontSize:'0.85rem', '&.Mui-selected': { bgcolor:'rgba(196,98,45,0.08)' } }}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Paper>
                    )}
                </Box>

                {/* Filter (manager only) */}
                {isManager && (
                    <Button size="small" variant={hasActiveFilters ? 'contained' : 'outlined'}
                            startIcon={<FilterListIcon/>}
                            onClick={() => setShowFilter(p => !p)}
                            sx={{ color:hasActiveFilters ? 'white':TERRACOTTA,
                                bgcolor:hasActiveFilters ? TERRACOTTA:'transparent',
                                borderColor:'rgba(196,98,45,0.35)' }}>
                        Filter {hasActiveFilters && `(active)`}
                    </Button>
                )}
                {hasActiveFilters && (
                    <Tooltip title="Clear filters">
                        <IconButton size="small" onClick={resetFilters} sx={{ color:'text.secondary' }}>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                )}
                <Box sx={{ flexGrow:1 }}/>
                <Typography variant="body2" fontWeight={700} sx={{ color: TERRACOTTA }}>
                    {displayedBookings.length} result{displayedBookings.length !== 1 ? 's' : ''}
                </Typography>
            </Stack>

            {/* Manager filter panel */}
            {isManager && (
                <Collapse in={showFilter}>
                    <Paper elevation={0} sx={{ p:2.5, mb:2.5, border:'1px solid rgba(196,98,45,0.15)', borderRadius:2,
                        bgcolor:'rgba(247,243,238,0.7)' }}>
                        <Typography variant="caption" fontWeight={700} sx={{ textTransform:'uppercase',
                            letterSpacing:'0.08em', color:'text.secondary', display:'block', mb:2 }}>
                            Filter Bookings
                        </Typography>
                        <Grid container spacing={2}>
                            {/* Customer */}
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField fullWidth size="small" label="Customer (name or email)"
                                           value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)}
                                           InputProps={{ startAdornment:<InputAdornment position="start"><SearchIcon sx={{ fontSize:'1rem', color:'text.secondary' }}/></InputAdornment> }}/>
                            </Grid>
                            {/* Tour */}
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField fullWidth size="small" label="Tour title"
                                           value={filterTour} onChange={e => setFilterTour(e.target.value)}/>
                            </Grid>
                            {/* Tour type */}
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField select fullWidth size="small" label="Tour type"
                                           value={filterTourType} onChange={e => setFilterTourType(e.target.value)}>
                                    <MenuItem value="">All</MenuItem>
                                    {TOUR_TYPES.map(tt => <MenuItem key={tt} value={tt}>{tt}</MenuItem>)}
                                </TextField>
                            </Grid>
                            {/* Place */}
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField fullWidth size="small" label="Place"
                                           value={filterPlace} onChange={e => setFilterPlace(e.target.value)}/>
                            </Grid>
                            {/* Date range */}
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField fullWidth size="small" label="Tour date from" type="date"
                                           value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                                           InputLabelProps={{ shrink:true }}/>
                            </Grid>
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField fullWidth size="small" label="Tour date to" type="date"
                                           value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                                           InputLabelProps={{ shrink:true }}/>
                            </Grid>
                            {/* Total range */}
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField fullWidth size="small" label="Min total ($)"
                                           type="number" value={filterTotalMin}
                                           onChange={e => setFilterTotalMin(e.target.value)}/>
                            </Grid>
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField fullWidth size="small" label="Max total ($)"
                                           type="number" value={filterTotalMax}
                                           onChange={e => setFilterTotalMax(e.target.value)}/>
                            </Grid>
                        </Grid>
                    </Paper>
                </Collapse>
            )}

            {/* Tabs */}
            <Paper sx={{ mb:3, overflow:'hidden', border:'1px solid', borderColor:'divider' }} elevation={0}>
                <Tabs value={tabValue} onChange={(_,v) => setTabValue(v)}
                      variant="scrollable" scrollButtons="auto"
                      sx={{ borderBottom:1, borderColor:'divider' }}>
                    {TAB_CATEGORIES.map(tab => (
                        <Tab key={tab} label={getTabLabel(tab)}/>
                    ))}
                </Tabs>
            </Paper>

            {/* Content */}
            <Box>
                {loading ? (
                    <Stack spacing={2}>
                        {Array.from({ length:3 }).map((_,i) => <BookingCardSkeleton key={i}/>)}
                    </Stack>
                ) : displayedBookings.length > 0 ? (
                    <Stack spacing={2}>
                        {displayedBookings.map(booking => {
                            const config    = getStatusConfig(booking.billStatus);
                            const isDeleted = booking.billStatus === 'DELETED';

                            // Format tour date if present, else fallback to createdAt
                            const displayDate = booking.tourDate
                                ? new Date(booking.tourDate + 'T00:00:00').toLocaleDateString('en-GB',
                                    { day:'numeric', month:'short', year:'numeric' })
                                : booking.createdAt
                                    ? new Date(booking.createdAt).toLocaleDateString('en-GB',
                                        { day:'numeric', month:'short', year:'numeric' })
                                    : '—';

                            const timeLabel = booking.slotStart
                                ? `${String(booking.slotStart).slice(0,5)}${booking.slotEnd ? ` – ${String(booking.slotEnd).slice(0,5)}`:''}` : null;

                            return (
                                <Card key={booking.id} elevation={0} sx={{
                                    display:'flex', flexDirection:{ xs:'column', sm:'row' },
                                    border:'1px solid', borderColor:'divider', overflow:'hidden',
                                    opacity:isDeleted ? 0.65:1, transition:'box-shadow 0.2s',
                                    '&:hover': { boxShadow:'0 4px 16px rgba(44,31,20,0.10)' }
                                }}>
                                    <CardContent sx={{ flex:1, p:2.5 }}>
                                        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1 }}>
                                            <Typography variant="h6" fontWeight="700" noWrap
                                                        sx={{ maxWidth:{ xs:'60%', sm:'70%' } }}>
                                                {booking.tourName}
                                            </Typography>
                                            <Chip label={config.label} color={config.color} size="small"
                                                  sx={{ fontWeight:700, fontSize:'0.7rem' }}/>
                                        </Box>

                                        <Stack direction="row" spacing={3} mt={1} flexWrap="wrap" gap={1}>
                                            {/* Date */}
                                            <Box display="flex" alignItems="center" gap={0.6}>
                                                <CalendarTodayIcon sx={{ fontSize:'0.9rem', color:'text.secondary' }}/>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>
                                                        {booking.tourDate ? 'Tour Date' : t('my_bookings.date')}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="600">{displayDate}</Typography>
                                                </Box>
                                            </Box>

                                            {/* Time */}
                                            {timeLabel && (
                                                <Box display="flex" alignItems="center" gap={0.6}>
                                                    <Box sx={{ width:14, height:14, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                        <Box component="span" sx={{ fontSize:'0.85rem', color:'text.secondary' }}>⏱</Box>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>Time</Typography>
                                                        <Typography variant="body2" fontWeight="600">{timeLabel}</Typography>
                                                    </Box>
                                                </Box>
                                            )}

                                            {/* Participants */}
                                            <Box display="flex" alignItems="center" gap={0.6}>
                                                <GroupIcon sx={{ fontSize:'0.9rem', color:'text.secondary' }}/>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>
                                                        {t('my_bookings.participants')}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="600">
                                                        {t('my_bookings.pax', { count:booking.participantCount })}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Tour type + place chips */}
                                            {isManager && booking.tourType && (
                                                <Chip label={booking.tourType} size="small" variant="outlined"
                                                      sx={{ fontSize:'0.65rem', height:20, alignSelf:'center' }}/>
                                            )}
                                            {isManager && booking.tourPlace && (
                                                <Chip label={booking.tourPlace} size="small" variant="outlined"
                                                      sx={{ fontSize:'0.65rem', height:20, alignSelf:'center', color:'text.secondary' }}/>
                                            )}
                                        </Stack>

                                        {isManager && booking.customerName && (
                                            <Box display="flex" alignItems="center" gap={1} mt={1.5}>
                                                <Avatar src={booking.customerImageUrl} alt={booking.customerName}
                                                        sx={{ width:22, height:22, fontSize:'0.65rem', bgcolor:'primary.light' }}>
                                                    {booking.customerName[0].toUpperCase()}
                                                </Avatar>
                                                <Typography variant="caption" color="text.secondary">
                                                    {booking.customerName}
                                                    {booking.customerEmail && ` · ${booking.customerEmail}`}
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>

                                    <Divider orientation="vertical" flexItem sx={{ display:{ xs:'none', sm:'block' } }}/>
                                    <Divider sx={{ display:{ xs:'block', sm:'none' } }}/>

                                    <Box sx={{
                                        p:2.5, display:'flex',
                                        flexDirection:{ xs:'row', sm:'column' },
                                        justifyContent:'space-between',
                                        alignItems:{ xs:'center', sm:'flex-end' },
                                        minWidth:{ xs: 0, sm: 180 },
                                        bgcolor:isDeleted ? 'grey.50':'rgba(250,246,240,0.6)',
                                    }}>
                                        <Box textAlign={{ xs:'left', sm:'right' }}>
                                            {booking.billType === 'BIKE_TRAVEL' && booking.billStatus === 'RESERVED' ? (
                                                <>
                                                    <Typography variant="caption" color="text.secondary" display="block">Already paid (30%)</Typography>
                                                    <Typography variant="h6" fontWeight={800} sx={{ color:'#C9973A', lineHeight:1.2 }}>
                                                        {format(booking.depositAmount, booking.currency)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt:0.5 }}>Balance due (70%)</Typography>
                                                    <Typography variant="body1" fontWeight={700} color="text.secondary">
                                                        {format(booking.totalPrice - booking.depositAmount, booking.currency)}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography variant="caption" color="text.secondary">{t('my_bookings.total')}</Typography>
                                                    <Typography variant="h5" color="primary" fontWeight="800" lineHeight={1.2}>
                                                        {format(booking.totalPrice, booking.currency)}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                        <Stack direction={{ xs:'row', sm:'column' }} spacing={1} sx={{ mt:{ sm:2 } }}>
                                            <Button
                                                variant={booking.billStatus === 'DRAFT' && !isManager ? 'contained':'outlined'}
                                                color={booking.billStatus   === 'DRAFT' && !isManager ? 'primary':'inherit'}
                                                size="small"
                                                component="a" href={`/booking/${booking.id}`}
                                                sx={{ whiteSpace:'nowrap' }}>
                                                {config.action}
                                            </Button>
                                            {/* Pay remaining balance for RESERVED BikeTravelBill bookings */}
                                            {booking.billStatus === 'RESERVED' && booking.billType === 'BIKE_TRAVEL' && !isManager && (() => {
                                                const deadline = booking.startDate
                                                    ? new Date(new Date(booking.startDate).getTime() - 15 * 24 * 60 * 60 * 1000)
                                                    : null;
                                                const deadlineStr = deadline
                                                    ? deadline.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })
                                                    : null;
                                                return (
                                                    <>
                                                        {deadlineStr && (
                                                            <Chip label={`Pay by ${deadlineStr}`} size="small"
                                                                  color="warning" variant="outlined"
                                                                  sx={{ fontSize:'0.65rem', height:20 }}/>
                                                        )}
                                                        <Button variant="contained" size="small"
                                                                disabled={payingBalanceId === booking.id}
                                                                onClick={() => handlePayBalance(booking.id)}
                                                                sx={{ bgcolor:'#4A6741', '&:hover':{ bgcolor:'#3a5233' },
                                                                    whiteSpace:'nowrap', fontWeight:700 }}>
                                                            {payingBalanceId === booking.id ? '…' : 'Pay Balance'}
                                                        </Button>
                                                    </>
                                                );
                                            })()}
                                            {isManager && isDeleted && (
                                                <Button variant="contained" color="error" size="small"
                                                        startIcon={<DeleteForeverIcon/>}
                                                        onClick={() => { setDeleteInput(''); setDeleteDialog({ open:true, bookingId:booking.id, tourName:booking.tourName }); }}>
                                                    {t('common.delete')}
                                                </Button>
                                            )}
                                        </Stack>
                                    </Box>
                                </Card>
                            );
                        })}
                    </Stack>
                ) : (
                    <Paper elevation={0} sx={{ p:6, textAlign:'center', bgcolor:'rgba(250,246,240,0.6)',
                        border:'1px dashed', borderColor:'divider' }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            {t('my_bookings.no_bookings', { status:getTabLabel(TAB_CATEGORIES[tabValue]).toLowerCase() })}
                        </Typography>
                        {!isManager && (
                            <Button variant="contained" sx={{ mt:2 }} component="a" href="/">
                                {t('my_bookings.browse_tours')}
                            </Button>
                        )}
                    </Paper>
                )}
            </Box>

            {/* ── Rental Bookings ── */}
            {!isManager && (
                <Box sx={{ mt: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                        <DirectionsBikeIcon sx={{ color: '#4A6741' }} />
                        <Typography variant="h5" fontWeight={800} sx={{ color: '#2C1F14' }}>
                            My Bike Rentals
                        </Typography>
                    </Box>

                    {rentalsLoading ? (
                        <Stack spacing={2}>
                            {[1, 2].map(i => <BookingCardSkeleton key={i} />)}
                        </Stack>
                    ) : rentals.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(74,103,65,0.04)',
                            border: '1px dashed rgba(74,103,65,0.3)', borderRadius: 2 }}>
                            <DirectionsBikeIcon sx={{ fontSize: '2.5rem', color: 'rgba(74,103,65,0.3)', mb: 1 }} />
                            <Typography variant="body1" color="text.secondary">No bike rentals yet.</Typography>
                            <Button variant="outlined" sx={{ mt: 2, borderColor: '#4A6741', color: '#4A6741' }}
                                    component="a" href="/rentals">
                                Rent a Bike
                            </Button>
                        </Paper>
                    ) : (() => {
                        // Group rentals by billId — one booking may have multiple bike types
                        const grouped = {};
                        rentals.forEach(r => {
                            const key = r.billId || r.id;
                            if (!grouped[key]) grouped[key] = [];
                            grouped[key].push(r);
                        });
                        const currentTabStatus = TAB_CATEGORIES[tabValue];
                        const rentalGroups = Object.entries(grouped).filter(([, group]) => {
                            const first = group[0];
                            const bs = first.billStatus;
                            // PAID tab also shows REFUND_REQUESTED (same as tour bookings)
                            if (currentTabStatus === 'PAID') return bs === 'PAID' || bs === 'REFUND_REQUESTED';
                            return bs === currentTabStatus;
                        });
                        if (rentalGroups.length === 0) return (
                            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(74,103,65,0.04)',
                                border: '1px dashed rgba(74,103,65,0.3)', borderRadius: 2 }}>
                                <DirectionsBikeIcon sx={{ fontSize: '2.5rem', color: 'rgba(74,103,65,0.3)', mb: 1 }} />
                                <Typography variant="body1" color="text.secondary">No bike rentals for this status.</Typography>
                            </Paper>
                        );
                        return (
                        <Stack spacing={2}>
                            {rentalGroups.map(([billId, group]) => {
                                const first = group[0];
                                const statusColors = {
                                    PENDING:   { color: 'warning', label: 'Pending' },
                                    CONFIRMED: { color: 'success', label: 'Confirmed' },
                                    ACTIVE:    { color: 'info',    label: 'Active' },
                                    COMPLETED: { color: 'default', label: 'Completed' },
                                    CANCELED:  { color: 'error',   label: 'Canceled' },
                                };
                                const sc = first.billStatus === 'PAID'
                                    ? { color: 'success', label: 'Confirmed' }
                                    : (statusColors[first.status] || { color: 'default', label: first.status });
                                const startDt = first.startTime ? new Date(first.startTime) : null;
                                const endDt   = first.endTime   ? new Date(first.endTime)   : null;
                                const dateStr = startDt
                                    ? startDt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                    : '—';
                                const timeStr = startDt && endDt
                                    ? `${startDt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} – ${endDt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                                    : null;
                                const durationLabel = first.durationType === 'HOURLY'
                                    ? `${first.days} hour${first.days !== 1 ? 's' : ''}`
                                    : `${first.days} day${first.days !== 1 ? 's' : ''}`;
                                const totalBikes = group.reduce((s, r) => s + (r.quantity || 0), 0);
                                const bikeTypeSummary = group
                                    .filter(r => r.quantity > 0)
                                    .map(r => `${r.quantity}× ${r.bikeType}`)
                                    .join(', ');

                                return (
                                    <Card key={billId} elevation={0} sx={{
                                        display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                                        border: '1px solid rgba(74,103,65,0.2)', overflow: 'hidden',
                                        transition: 'box-shadow 0.2s',
                                        '&:hover': { boxShadow: '0 4px 16px rgba(74,103,65,0.12)' },
                                    }}>
                                        <CardContent sx={{ flex: 1, p: 2.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="h6" fontWeight={700}>
                                                    Bike Rental · {totalBikes} bike{totalBikes !== 1 ? 's' : ''}
                                                </Typography>
                                                <Chip label={sc.label} color={sc.color} size="small"
                                                      sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                                            </Box>
                                            {bikeTypeSummary && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {bikeTypeSummary}
                                                </Typography>
                                            )}
                                            <Stack direction="row" spacing={2.5} flexWrap="wrap" gap={1}>
                                                <Box display="flex" alignItems="center" gap={0.6}>
                                                    <CalendarTodayIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>Date</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{dateStr}</Typography>
                                                    </Box>
                                                </Box>
                                                {timeStr && (
                                                    <Box display="flex" alignItems="center" gap={0.6}>
                                                        <AccessTimeIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>Time</Typography>
                                                            <Typography variant="body2" fontWeight={600}>{timeStr}</Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                                <Box display="flex" alignItems="center" gap={0.6}>
                                                    <DirectionsBikeIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>Duration</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{durationLabel}</Typography>
                                                    </Box>
                                                </Box>
                                            </Stack>
                                            {first.districtAddress && (
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                    📍 {first.districtAddress}
                                                </Typography>
                                            )}
                                        </CardContent>
                                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                                        <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
                                        <Box sx={{ p: 2.5, display: 'flex', flexDirection: { xs: 'row', sm: 'column' },
                                            justifyContent: 'space-between', alignItems: { xs: 'center', sm: 'flex-end' },
                                            minWidth: { xs: 0, sm: 160 }, bgcolor: 'rgba(74,103,65,0.04)' }}>
                                            {first.billTotalAmount != null && (
                                                <Box textAlign={{ xs: 'left', sm: 'right' }} sx={{ mb: { sm: 1.5 } }}>
                                                    <Typography variant="caption" color="text.secondary">{t('my_bookings.total')}</Typography>
                                                    <Typography variant="h6" fontWeight={800} sx={{ color: '#4A6741', lineHeight: 1.2 }}>
                                                        {format(first.billTotalAmount, first.billCurrency || 'EUR')}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Button variant="outlined" size="small"
                                                    component="a" href={`/booking/${billId}`}
                                                    sx={{ borderColor: '#4A6741', color: '#4A6741', whiteSpace: 'nowrap', fontWeight: 700 }}>
                                                View Details
                                            </Button>
                                        </Box>
                                    </Card>
                                );
                            })}
                        </Stack>
                        );
                    })()}
                </Box>
            )}

            {/* Hard-Delete Dialog */}
            <Dialog open={deleteDialog.open}
                    onClose={() => !deleteLoading && setDeleteDialog({ open:false, bookingId:null, tourName:'' })}
                    maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight:'bold', color:'error.main' }}>
                    {t('my_bookings.delete_title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb:2 }}>
                        {t('my_bookings.delete_text', { tour:deleteDialog.tourName })}
                    </DialogContentText>
                    <DialogContentText sx={{ mb:2 }}>{t('my_bookings.type_delete')}</DialogContentText>
                    <TextField autoFocus fullWidth variant="outlined" placeholder="DELETE"
                               value={deleteInput} onChange={e => setDeleteInput(e.target.value)}
                               error={deleteInput.length > 0 && deleteInput !== 'DELETE'}
                               helperText={deleteInput.length > 0 && deleteInput !== 'DELETE' ? t('my_bookings.must_match') : ''}/>
                </DialogContent>
                <DialogActions sx={{ px:3, pb:2 }}>
                    <Button onClick={() => setDeleteDialog({ open:false, bookingId:null, tourName:'' })} disabled={deleteLoading}>
                        {t('my_bookings.cancel_btn')}
                    </Button>
                    <Button variant="contained" color="error" onClick={handleHardDelete}
                            disabled={deleteInput !== 'DELETE' || deleteLoading}
                            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit"/> : <DeleteForeverIcon/>}>
                        {deleteLoading ? t('my_bookings.deleting') : t('my_bookings.delete_btn')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
        </Box>
    );
};

export default MyBookings;